import type {
  LockerverseAuction,
  LockerverseAuctionItem,
  LockerverseAuctionPricing,
} from "./auction-types.ts";
import { deepFreeze } from "./deep-freeze.ts";

function assertCents(value: number) {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new TypeError("Auction amounts must be non-negative safe integers.");
  }
}

/** Local estimate. Send expected amounts with the submission for server validation. */
export function calculateLockerverseAuctionPricing(
  auction: Pick<LockerverseAuction, "serviceFeeEnabled" | "pricingPolicy">,
  subtotalCents: number,
  includeServiceFee = false,
  additionalChargeCents = 0
): LockerverseAuctionPricing {
  assertCents(subtotalCents);
  assertCents(additionalChargeCents);
  if (includeServiceFee && !auction.serviceFeeEnabled) {
    throw new TypeError("The service fee is not available for this auction.");
  }
  const serviceFeeRateBasisPoints = includeServiceFee
    ? auction.pricingPolicy.serviceFeeRateBasisPoints
    : 0;
  assertCents(serviceFeeRateBasisPoints);
  const serviceFeeCents = Math.round(
    (subtotalCents * serviceFeeRateBasisPoints) / 10_000
  );
  const totalCents = subtotalCents + serviceFeeCents + additionalChargeCents;
  assertCents(serviceFeeCents);
  assertCents(totalCents);
  const pricing: LockerverseAuctionPricing = {
    serviceFeeCents,
    serviceFeeRateBasisPoints,
    subtotalCents,
    totalCents,
  };
  if (additionalChargeCents > 0) {
    pricing.additionalChargeCents = additionalChargeCents;
  }
  return deepFreeze(pricing);
}

/** Snapshot estimate. Another accepted bid can increase the server minimum. */
export function getLockerverseAuctionMinimumBid(
  item: Pick<
    LockerverseAuctionItem,
    "minBidAmount" | "bidIncrementAmount" | "maxBidAmount"
  >
): number {
  const minimum = item.minBidAmount ?? 100;
  const increment = item.bidIncrementAmount ?? 100;
  assertCents(minimum);
  assertCents(increment);
  if (item.maxBidAmount === null) {
    return minimum;
  }
  assertCents(item.maxBidAmount);
  const amount = Math.max(minimum, item.maxBidAmount + increment);
  assertCents(amount);
  return amount;
}
